import React from 'react';
import { Typography, Upload, Radio } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { ActionButton } from '@doer/frontend-components/buttons';
import { FormInput } from '../../ui/inputs/form-input';
import { CustomTooltip } from './custom-tooltip';
import type { BaseFieldProps } from '../../../types/form-renderer';
import type { VideoField as VideoFieldType } from '../../../types/templates';
import type { VideoUrl } from '../../../types/banner/video-url';

const { Text } = Typography;

type Props = BaseFieldProps<VideoFieldType>;

const isVideoUrl = (v: unknown): v is VideoUrl => typeof v === 'object' && v !== null && 'originalUrl' in v;

export const VideoField: React.FC<Props> = ({ field, value, onChange, commonStyle, labelStyle, error }) => {
  const [videoInputType, setVideoInputType] = React.useState<'link' | 'upload'>('link');
  const [selectedFileName, setSelectedFileName] = React.useState<string | null>(null);

  const handleUrlChange = (url: string) => onChange({ originalUrl: url });

  const handleFileSelect = (file: File) => {
    setSelectedFileName(file.name);
    onChange({ file });
  };

  const currentUrl = isVideoUrl(value) ? value.originalUrl : '';

  return (
    <div style={commonStyle}>
      <Text type='secondary' style={{ ...labelStyle, textTransform: 'uppercase' }}>
        {field.label}
      </Text>

      <div style={{ marginBottom: 12 }}>
        <Radio.Group
          value={videoInputType}
          onChange={e => {
            setVideoInputType(e.target.value);
            if (e.target.value === 'link') setSelectedFileName(null);
          }}
          style={{ display: 'flex', gap: 8 }}
        >
          <Radio value='link' style={{ fontSize: commonStyle.fontSize }}>
            URL del video
          </Radio>
          <Radio value='upload' style={{ fontSize: commonStyle.fontSize }}>
            Subir archivo
          </Radio>
        </Radio.Group>
      </div>

      {videoInputType === 'link' ? (
        <CustomTooltip field={field}>
          <FormInput
            size='S'
            placeholder='https://cdn.filestackcontent.com/ejemplo.mp4'
            value={currentUrl}
            onChange={e => handleUrlChange(e.target.value)}
            status={error ? 'error' : undefined}
          />
        </CustomTooltip>
      ) : (
        <CustomTooltip field={field}>
          <Upload
            accept='video/mp4'
            beforeUpload={file => {
              handleFileSelect(file);
              return false;
            }}
            showUploadList={false}
          >
            <ActionButton variant='outline' size='S'>
              <UploadOutlined />
              {selectedFileName
                ? selectedFileName.length > 20
                  ? `${selectedFileName.substring(0, 20)}...`
                  : selectedFileName
                : 'Seleccionar video MP4'}
            </ActionButton>
          </Upload>
        </CustomTooltip>
      )}

      {error && (
        <Text type='danger' style={{ fontSize: 12, marginTop: 4 }}>
          {error}
        </Text>
      )}
    </div>
  );
};
